// Animation d'entrée des éléments de scène (calques SVG et items d'interface).
//
// Module PUR, sans DOM ni Meteor : il est partagé par le serveur (normalisation des réglages),
// le client du hub (inspecteur + lecteur), et sert de référence au portage diffusion et Unity.
// Tout le mouvement est décrit par `echantillon_animation_entree (réglages, contexte, t)`,
// une fonction du temps : le web en tire des images clés jouées par le navigateur (Web Animations,
// sans JavaScript par image), Unity pourra l'évaluer directement à chaque image.
//
// Repère : coordonnées utilisateur de la racine SVG de la scène (unités du viewBox).
// Chronologie : [délai] → trajet (duree_ms, easing) → arrivée (arrivee_duree_ms : rebonds et
// déformation d'impact). Le délai est compté depuis l'ouverture de la scène.

export const ANIMATION_ENTREE_TYPES = Object.freeze ([
  {value : "deplacement_2d", label : "Déplacement 2D"},
  {value : "deplacement_3d", label : "Déplacement 3D"},
  {value : "fondu",          label : "Fondu"},
]);

export const ANIMATION_ENTREE_DEPARTS = Object.freeze ([
  {value : "haut",   label : "Hors champ, en haut"},
  {value : "bas",    label : "Hors champ, en bas"},
  {value : "gauche", label : "Hors champ, à gauche"},
  {value : "droite", label : "Hors champ, à droite"},
  {value : "precis", label : "Décalage précis"},
]);

export const ANIMATION_ENTREE_EASINGS = Object.freeze ([
  {value : "lineaire",           label : "Linéaire"},
  {value : "acceleration",       label : "Accélération"},
  {value : "acceleration_forte", label : "Accélération forte (chute)"},
  {value : "deceleration",       label : "Décélération"},
  {value : "deceleration_forte", label : "Décélération forte"},
  {value : "douce",              label : "Douce (entrée et sortie)"},
  {value : "douce_forte",        label : "Douce forte"},
  {value : "depassement",        label : "Dépassement puis retour"},
]);

export const ANIMATION_ENTREE_AXES = Object.freeze ([
  {value : "vertical",   label : "Vertical"},
  {value : "horizontal", label : "Horizontal"},
]);

export const ANIMATION_ENTREE_ANCRES = Object.freeze ([
  {value : "haut",   label : "Haut"},
  {value : "centre", label : "Centre"},
  {value : "bas",    label : "Bas"},
  {value : "gauche", label : "Gauche"},
  {value : "droite", label : "Droite"},
]);

export const ANIMATION_ENTREE_PROFILS_DEFORMATION = Object.freeze ([
  {value : "ressort", label : "Ressort (s'étire puis se tasse)"},
  {value : "rebonds", label : "Rebonds (s'étire seulement)"},
]);

// Valeurs par défaut reprises du réglage de référence de Yann (bouton_pub de NIVEAU1, 01/10/2026) :
// chute depuis le haut avec léger dépassement, puis étirement vertical ancré en haut.
export const ANIMATION_ENTREE_DEFAUTS = Object.freeze ({
  actif : false,
  type : "deplacement_2d",
  delai_ms : 500,
  duree_ms : 500,
  easing : "depassement",
  depassement_pct : 1,
  fondu_simultane : false,
  depart : "haut",
  depart_dx : 0,
  depart_dy : -200,
  depart_dz : 1500,
  perspective : 1200,
  depart_rotation_deg : 0,
  depart_rotation_x_deg : 0,
  depart_rotation_y_deg : 0,
  arrivee_duree_ms : 800,
  rebonds_nombre : 0,
  rebonds_amplitude_pct : 0,
  rebonds_amortissement : 0.95,
  etirement_trajet_pct : 0,
  deformation_pct : 2,
  deformation_axe : "vertical",
  deformation_ancre : "haut",
  deformation_profil : "ressort",
  deformation_oscillations : 4,
  deformation_amortissement : 0.6,
  deformation_avance_ms : 0,
  deformation_volume : false,
});

export const ANIMATION_ENTREE_LIMITES = Object.freeze ({
  delai_ms : {min : 0, max : 30000, step : 10},
  duree_ms : {min : 0, max : 10000, step : 10},
  depassement_pct : {min : 1, max : 100, step : 1},
  depart_dx : {min : -20000, max : 20000, step : 1},
  depart_dy : {min : -20000, max : 20000, step : 1},
  depart_dz : {min : -900, max : 20000, step : 10},
  perspective : {min : 100, max : 20000, step : 10},
  depart_rotation_deg : {min : -1080, max : 1080, step : 1},
  depart_rotation_x_deg : {min : -180, max : 180, step : 1},
  depart_rotation_y_deg : {min : -180, max : 180, step : 1},
  arrivee_duree_ms : {min : 0, max : 10000, step : 10},
  rebonds_nombre : {min : 0, max : 8, step : 1},
  rebonds_amplitude_pct : {min : 0, max : 100, step : 1},
  rebonds_amortissement : {min : 0.05, max : 0.95, step : 0.05},
  etirement_trajet_pct : {min : 0, max : 100, step : 1},
  deformation_pct : {min : -80, max : 200, step : 1},
  deformation_oscillations : {min : 1, max : 10, step : 1},
  deformation_amortissement : {min : 0.05, max : 0.95, step : 0.05},
  deformation_avance_ms : {min : 0, max : 5000, step : 10},
});

// Cadence des images clés générées pour le web : l'interpolation entre deux images est linéaire,
// 60 images par seconde suffisent à rendre fidèlement easing, rebonds et déformations.
export const ANIMATION_ENTREE_IMAGES_PAR_SECONDE = 60;

function borne_nombre (pValeur, pMin, pMax, pDefaut)
  {
    const tBrut = Number (pValeur);
    const tNombre = Number.isFinite (tBrut) ? tBrut : pDefaut;
    return Math.max (pMin, Math.min (pMax, tNombre));
  }

function arrondit_nombre (pValeur, pPrecision = 3)
  {
    const tFacteur = Math.pow (10, Math.max (0, pPrecision | 0));
    return Math.round ((Number (pValeur) || 0) * tFacteur) / tFacteur;
  }

function valeur_liste (pValeur, pListe, pDefaut)
  {
    return pListe.some ((pOption) => pOption.value === pValeur) ? pValeur : pDefaut;
  }

export function normalise_animation_entree (pValeur)
  {
    const tSource = pValeur && typeof pValeur === "object" && !Array.isArray (pValeur) ? pValeur : {};
    const D = ANIMATION_ENTREE_DEFAUTS;
    const L = ANIMATION_ENTREE_LIMITES;
    const tNombre = (pCle, pPrecision = 3) =>
      arrondit_nombre (borne_nombre (tSource[pCle], L[pCle].min, L[pCle].max, D[pCle]), pPrecision);
    return {
      actif : tSource.actif === true,
      type : valeur_liste (tSource.type, ANIMATION_ENTREE_TYPES, D.type),
      delai_ms : tNombre ("delai_ms", 0),
      duree_ms : tNombre ("duree_ms", 0),
      easing : valeur_liste (tSource.easing, ANIMATION_ENTREE_EASINGS, D.easing),
      depassement_pct : tNombre ("depassement_pct", 1),
      fondu_simultane : tSource.fondu_simultane === true,
      depart : valeur_liste (tSource.depart, ANIMATION_ENTREE_DEPARTS, D.depart),
      depart_dx : tNombre ("depart_dx", 2),
      depart_dy : tNombre ("depart_dy", 2),
      depart_dz : tNombre ("depart_dz", 2),
      perspective : tNombre ("perspective", 2),
      depart_rotation_deg : tNombre ("depart_rotation_deg", 2),
      depart_rotation_x_deg : tNombre ("depart_rotation_x_deg", 2),
      depart_rotation_y_deg : tNombre ("depart_rotation_y_deg", 2),
      arrivee_duree_ms : tNombre ("arrivee_duree_ms", 0),
      rebonds_nombre : Math.round (tNombre ("rebonds_nombre", 0)),
      rebonds_amplitude_pct : tNombre ("rebonds_amplitude_pct", 1),
      rebonds_amortissement : tNombre ("rebonds_amortissement", 3),
      etirement_trajet_pct : tNombre ("etirement_trajet_pct", 1),
      deformation_pct : tNombre ("deformation_pct", 1),
      deformation_axe : valeur_liste (tSource.deformation_axe, ANIMATION_ENTREE_AXES, D.deformation_axe),
      deformation_ancre : valeur_liste (tSource.deformation_ancre, ANIMATION_ENTREE_ANCRES, D.deformation_ancre),
      deformation_profil : valeur_liste (tSource.deformation_profil, ANIMATION_ENTREE_PROFILS_DEFORMATION, D.deformation_profil),
      deformation_oscillations : Math.round (tNombre ("deformation_oscillations", 0)),
      deformation_amortissement : tNombre ("deformation_amortissement", 3),
      deformation_avance_ms : tNombre ("deformation_avance_ms", 0),
      deformation_volume : typeof tSource.deformation_volume === "boolean" ? tSource.deformation_volume : D.deformation_volume,
    };
  }

export function animation_entree_active (pValeur)
  {
    return !!(pValeur && typeof pValeur === "object" && pValeur.actif === true);
  }

function est_deplacement (pA)
  {
    return pA.type === "deplacement_2d" || pA.type === "deplacement_3d";
  }

function rebonds_actifs (pA)
  {
    return est_deplacement (pA) && pA.rebonds_nombre > 0 && pA.rebonds_amplitude_pct > 0 && pA.arrivee_duree_ms > 0;
  }

function deformation_active (pA)
  {
    return Math.abs (pA.deformation_pct) > 0 && pA.arrivee_duree_ms > 0;
  }

// Chronologie (ms, après le délai) :
// - trajet de 0 à duree_ms ;
// - rebonds de position : après le trajet, pendant arrivee_duree_ms ;
// - déformation : dès que l'élément atteint sa place pour la première fois (avant la fin du
//   trajet avec « dépassement »), avancée de deformation_avance_ms, pendant arrivee_duree_ms.
export function chronologie_animation_entree (pValeur)
  {
    const tA = normalise_animation_entree (pValeur);
    const tTrajet = tA.duree_ms;
    const tArrivee = tA.type === "fondu" ? 1 : instant_arrivee_easing (tA.easing, tA.depassement_pct);
    const tDebutDeformation = Math.max (0, (tArrivee * tTrajet) - tA.deformation_avance_ms);
    let tFin = tTrajet;
    if (rebonds_actifs (tA)) tFin = Math.max (tFin, tTrajet + tA.arrivee_duree_ms);
    if (deformation_active (tA)) tFin = Math.max (tFin, tDebutDeformation + tA.arrivee_duree_ms);
    return {trajet_ms : tTrajet, debut_deformation_ms : tDebutDeformation, duree_ms : tFin};
  }

// Durée jouée après le délai.
export function duree_animation_entree_ms (pValeur)
  {
    return chronologie_animation_entree (pValeur).duree_ms;
  }

// ─── Easings ─────────────────────────────────────────────────────────────────

// « Dépassement » = easing back-out : f(t) = 1 + (c+1)(t-1)³ + c(t-1)².
// Son maximum vaut 1 + 4c³ / (27 (c+1)²) : on en déduit c pour un dépassement donné
// (fraction du trajet), par dichotomie (la fonction est croissante en c).
const gCacheDepassement = new Map ();

export function coefficient_depassement (pDepassementPct)
  {
    const tCible = Math.max (0.001, Number (pDepassementPct) || 10) / 100;
    if (gCacheDepassement.has (tCible)) return gCacheDepassement.get (tCible);
    let tMin = 0;
    let tMax = 1000;
    for (let i = 0; i < 80; i++)
      {
        const c = (tMin + tMax) / 2;
        const tPic = (4 * c * c * c) / (27 * (c + 1) * (c + 1));
        if (tPic < tCible) tMin = c;
        else tMax = c;
      }
    const tC = (tMin + tMax) / 2;
    gCacheDepassement.set (tCible, tC);
    return tC;
  }

// Instant (0..1 du trajet) où l'élément atteint sa place pour la première fois.
export function instant_arrivee_easing (pNom, pDepassementPct)
  {
    if (pNom !== "depassement") return 1;
    return 1 / (coefficient_depassement (pDepassementPct) + 1);
  }

export function easing_animation_entree (pNom, pT, pDepassementPct = 10)
  {
    const t = Math.max (0, Math.min (1, pT));
    switch (pNom)
      {
        case "acceleration" :       return t * t;
        case "acceleration_forte" : return t * t * t;
        case "deceleration" :       return 1 - ((1 - t) * (1 - t));
        case "deceleration_forte" : return 1 - Math.pow (1 - t, 3);
        case "douce" :              return 0.5 - (Math.cos (Math.PI * t) / 2);
        case "douce_forte" :        return t < 0.5 ? 4 * t * t * t : 1 - (Math.pow (-2 * t + 2, 3) / 2);
        case "depassement" :
          {
            const c1 = coefficient_depassement (pDepassementPct);
            const c3 = c1 + 1;
            return 1 + (c3 * Math.pow (t - 1, 3)) + (c1 * Math.pow (t - 1, 2));
          }
        default : return t;
      }
  }

// ─── Oscillations amorties (rebonds de position et déformation d'impact) ───────

// Découpe [0, 1] en `pNombre` segments d'amplitudes a_k = r^k ; chaque segment dure
// proportionnellement à `pPoidsDuree (a_k)`. Renvoie {indice, local, amplitude} pour un instant u.
function segment_oscillation (pU, pNombre, pAmortissement, pPoidsDuree)
  {
    const tN = Math.max (1, pNombre | 0);
    const tPoids = [];
    let tTotal = 0;
    for (let k = 0; k < tN; k++)
      {
        const tAmp = Math.pow (pAmortissement, k);
        const tP = Math.max (1e-6, pPoidsDuree (tAmp));
        tPoids.push ({amplitude : tAmp, poids : tP});
        tTotal += tP;
      }
    const u = Math.max (0, Math.min (1, pU));
    let tCumul = 0;
    for (let k = 0; k < tN; k++)
      {
        const tPart = tPoids[k].poids / tTotal;
        if (u <= tCumul + tPart || k === tN - 1)
          return {
            indice : k,
            local : tPart > 0 ? Math.max (0, Math.min (1, (u - tCumul) / tPart)) : 1,
            amplitude : tPoids[k].amplitude,
          };
        tCumul += tPart;
      }
    return {indice : tN - 1, local : 1, amplitude : 0};
  }

// Rebonds de balle : chaque rebond est une parabole (retour vers le départ puis nouvel impact),
// de hauteur décroissante ; la durée d'un rebond suit la racine de sa hauteur (chute libre).
function retour_rebonds (pA, pU)
  {
    if (pA.rebonds_nombre <= 0 || pA.rebonds_amplitude_pct <= 0) return 0;
    const tSeg = segment_oscillation (pU, pA.rebonds_nombre, pA.rebonds_amortissement, (pAmp) => Math.sqrt (pAmp));
    const v = tSeg.local;
    return (pA.rebonds_amplitude_pct / 100) * tSeg.amplitude * 4 * v * (1 - v);
  }

// Déformation d'impact : demi-ondes sinusoïdales d'amplitude décroissante, alternées (ressort)
// ou toutes dans le même sens (rebonds). Renvoie l'écart relatif d'échelle sur l'axe choisi.
function ecart_deformation (pA, pU)
  {
    if (!pA.deformation_pct) return 0;
    const tSeg = segment_oscillation (pU, pA.deformation_oscillations, pA.deformation_amortissement, (pAmp) => 0.55 + (0.45 * pAmp));
    const tSigne = pA.deformation_profil === "ressort" && (tSeg.indice % 2 === 1) ? -1 : 1;
    return tSigne * (pA.deformation_pct / 100) * tSeg.amplitude * Math.sin (Math.PI * tSeg.local);
  }

// ─── Contexte ────────────────────────────────────────────────────────────────

// Décalage de départ (repère scène). « Hors champ » place l'élément juste à l'extérieur de la vue
// de la scène, avec une marge de 2 % de la vue ; le calcul est refait à chaque lecture, il suit
// donc les déplacements de l'élément et les changements de cadrage.
export function decalage_depart_animation_entree (pA, pBBox, pVue)
  {
    if (pA.depart === "precis" || !pBBox || !pVue)
      return {dx : pA.depart_dx, dy : pA.depart_dy};
    const tMargeX = pVue.w * 0.02;
    const tMargeY = pVue.h * 0.02;
    switch (pA.depart)
      {
        case "haut" :   return {dx : 0, dy : (pVue.y - tMargeY) - (pBBox.y + pBBox.h)};
        case "bas" :    return {dx : 0, dy : (pVue.y + pVue.h + tMargeY) - pBBox.y};
        case "gauche" : return {dx : (pVue.x - tMargeX) - (pBBox.x + pBBox.w), dy : 0};
        case "droite" : return {dx : (pVue.x + pVue.w + tMargeX) - pBBox.x, dy : 0};
        default :       return {dx : pA.depart_dx, dy : pA.depart_dy};
      }
  }

function point_ancre (pAncre, pBBox)
  {
    const cx = pBBox.x + (pBBox.w / 2);
    const cy = pBBox.y + (pBBox.h / 2);
    switch (pAncre)
      {
        case "haut" :   return {x : cx, y : pBBox.y};
        case "bas" :    return {x : cx, y : pBBox.y + pBBox.h};
        case "gauche" : return {x : pBBox.x, y : cy};
        case "droite" : return {x : pBBox.x + pBBox.w, y : cy};
        default :       return {x : cx, y : cy};
      }
  }

// ─── Matrices affines 2D {a, b, c, d, e, f} (convention SVG) ──────────────────

function mat (a, b, c, d, e, f) { return {a, b, c, d, e, f}; }
const MAT_ID = Object.freeze (mat (1, 0, 0, 1, 0, 0));

export function multiplie_matrices_animation_entree (m, n)
  {
    return mat (
      (m.a * n.a) + (m.c * n.b),
      (m.b * n.a) + (m.d * n.b),
      (m.a * n.c) + (m.c * n.d),
      (m.b * n.c) + (m.d * n.d),
      (m.a * n.e) + (m.c * n.f) + m.e,
      (m.b * n.e) + (m.d * n.f) + m.f
    );
  }

function translation (x, y) { return mat (1, 0, 0, 1, x, y); }
function echelle_autour (sx, sy, px, py) { return mat (sx, 0, 0, sy, px - (sx * px), py - (sy * py)); }
function rotation_autour (pDeg, px, py)
  {
    const r = pDeg * Math.PI / 180;
    const cs = Math.cos (r);
    const sn = Math.sin (r);
    return mat (cs, sn, -sn, cs, px - (cs * px) + (sn * py), py - (sn * px) - (cs * py));
  }

// Étirement le long d'une direction quelconque (angle en radians), à volume conservé.
function etirement_direction (pFacteur, pAngle, px, py)
  {
    const tR = rotation_autour (pAngle * 180 / Math.PI, px, py);
    const tRi = rotation_autour (-pAngle * 180 / Math.PI, px, py);
    const tS = echelle_autour (pFacteur, 1 / pFacteur, px, py);
    return multiplie_matrices_animation_entree (tR, multiplie_matrices_animation_entree (tS, tRi));
  }

function signe_non_nul (pValeur, pMin = 0.01)
  {
    if (Math.abs (pValeur) >= pMin) return pValeur;
    return pValeur < 0 ? -pMin : pMin;
  }

// ─── Échantillonnage ─────────────────────────────────────────────────────────

// Vitesse de progression maximale de l'easing (pour normaliser l'étirement en vol).
function vitesse_max_easing (pNom, pDepassementPct)
  {
    let tMax = 0;
    const tPas = 1 / 64;
    for (let i = 0; i < 64; i++)
      {
        const tV = Math.abs (easing_animation_entree (pNom, (i + 1) * tPas, pDepassementPct) - easing_animation_entree (pNom, i * tPas, pDepassementPct)) / tPas;
        if (tV > tMax) tMax = tV;
      }
    return tMax || 1;
  }

// Contexte attendu : {bbox : {x, y, w, h}, vue : {x, y, w, h}, opacite : 0..1}
// (bbox de l'élément à sa place normale, vue de la scène, opacité normale de l'élément).
// Renvoie {matrice, opacite} pour un temps t (ms) compté APRÈS le délai.
// La matrice s'applique dans le repère scène, AVANT la transformation normale de l'élément.
export function echantillon_animation_entree (pValeur, pContexte, pTempsMs, pCache = null)
  {
    const tA = pCache && pCache.reglages ? pCache.reglages : normalise_animation_entree (pValeur);
    const tChrono = pCache && pCache.chronologie ? pCache.chronologie : chronologie_animation_entree (tA);
    const tBBox = pContexte && pContexte.bbox ? pContexte.bbox : {x : 0, y : 0, w : 1, h : 1};
    const tVue = pContexte && pContexte.vue ? pContexte.vue : null;
    const tOpaciteBase = pContexte && Number.isFinite (Number (pContexte.opacite)) ? Number (pContexte.opacite) : 1;
    const tTrajet = tA.duree_ms;
    const tArrivee = tA.arrivee_duree_ms;
    const t = Math.max (0, Number (pTempsMs) || 0);
    const cx = tBBox.x + (tBBox.w / 2);
    const cy = tBBox.y + (tBBox.h / 2);

    // Progression le long du trajet : 0 = départ, 1 = place normale (peut déborder avec « dépassement »,
    // ou revenir en arrière pendant les rebonds).
    const tLin = tTrajet > 0 ? Math.min (1, t / tTrajet) : 1;
    let tProgression = easing_animation_entree (tA.easing, tLin, tA.depassement_pct);
    let tVitesse = 0;
    let tEcartDeformation = 0;
    if (t < tTrajet)
      {
        const h = 0.004;
        const tAvant = easing_animation_entree (tA.easing, Math.max (0, tLin - h), tA.depassement_pct);
        const tApres = easing_animation_entree (tA.easing, Math.min (1, tLin + h), tA.depassement_pct);
        const tMax = pCache && pCache.vitesseMax ? pCache.vitesseMax : vitesse_max_easing (tA.easing, tA.depassement_pct);
        tVitesse = Math.min (1, Math.abs (tApres - tAvant) / (Math.min (1, tLin + h) - Math.max (0, tLin - h)) / tMax);
      }
    else
      {
        tProgression = 1;
        if (rebonds_actifs (tA))
          tProgression = 1 - retour_rebonds (tA, Math.min (1, (t - tTrajet) / tArrivee));
      }
    if (deformation_active (tA) && t >= tChrono.debut_deformation_ms)
      tEcartDeformation = ecart_deformation (tA, Math.min (1, (t - tChrono.debut_deformation_ms) / tArrivee));

    let tOpacite = tOpaciteBase;
    let tMatrice = MAT_ID;

    if (tA.type === "fondu")
      tOpacite = tOpaciteBase * Math.max (0, Math.min (1, tProgression));
    else
      {
        if (tA.fondu_simultane)
          tOpacite = tOpaciteBase * Math.max (0, Math.min (1, tLin * 1.6));
        const tDepart = decalage_depart_animation_entree (tA, tBBox, tVue);
        const tReste = 1 - tProgression;
        let ox = tDepart.dx * tReste;
        let oy = tDepart.dy * tReste;
        let tEchelleProj = 1;
        let tRaccourciX = 1;
        let tRaccourciY = 1;
        if (tA.type === "deplacement_3d")
          {
            // Projection perspective simulée : profondeur → échelle, rotations X/Y → raccourcis.
            const z = tA.depart_dz * tReste;
            const tFocale = Math.max (1, tA.perspective);
            const k = tFocale / Math.max (tFocale * 0.1, tFocale + z);
            const vx = tVue ? tVue.x + (tVue.w / 2) : cx;
            const vy = tVue ? tVue.y + (tVue.h / 2) : cy;
            ox = vx + ((cx + ox - vx) * k) - cx;
            oy = vy + ((cy + oy - vy) * k) - cy;
            tEchelleProj = k;
            tRaccourciX = signe_non_nul (Math.cos (tA.depart_rotation_y_deg * tReste * Math.PI / 180));
            tRaccourciY = signe_non_nul (Math.cos (tA.depart_rotation_x_deg * tReste * Math.PI / 180));
          }
        // Ordre (du plus intérieur au plus extérieur) : étirement en vol, échelle/raccourcis
        // autour du centre, rotation dans le plan, translation.
        if (tA.etirement_trajet_pct > 0 && tVitesse > 0 && (tDepart.dx || tDepart.dy))
          {
            const tFacteur = 1 + ((tA.etirement_trajet_pct / 100) * tVitesse);
            tMatrice = etirement_direction (tFacteur, Math.atan2 (tDepart.dy, tDepart.dx), cx, cy);
          }
        if (tEchelleProj !== 1 || tRaccourciX !== 1 || tRaccourciY !== 1)
          tMatrice = multiplie_matrices_animation_entree (
            echelle_autour (tEchelleProj * tRaccourciX, tEchelleProj * tRaccourciY, cx, cy), tMatrice);
        const tRotation = tA.depart_rotation_deg * tReste;
        if (tRotation)
          tMatrice = multiplie_matrices_animation_entree (rotation_autour (tRotation, cx, cy), tMatrice);
        if (ox || oy)
          tMatrice = multiplie_matrices_animation_entree (translation (ox, oy), tMatrice);
      }

    if (tEcartDeformation)
      {
        const tAncre = point_ancre (tA.deformation_ancre, tBBox);
        const tAxe = 1 + tEcartDeformation;
        const tAutre = tA.deformation_volume ? 1 / signe_non_nul (tAxe, 0.05) : 1;
        const tDef = tA.deformation_axe === "horizontal"
          ? echelle_autour (signe_non_nul (tAxe), tAutre, tAncre.x, tAncre.y)
          : echelle_autour (tAutre, signe_non_nul (tAxe), tAncre.x, tAncre.y);
        // La déformation d'impact s'applique à l'élément arrivé : elle est la plus intérieure.
        tMatrice = multiplie_matrices_animation_entree (tMatrice, tDef);
      }

    return {matrice : tMatrice, opacite : tOpacite};
  }

// Images clés régulières (offset 0..1) couvrant trajet + arrivée.
export function images_cles_animation_entree (pValeur, pContexte, pImagesParSeconde = ANIMATION_ENTREE_IMAGES_PAR_SECONDE)
  {
    const tA = normalise_animation_entree (pValeur);
    const tChrono = chronologie_animation_entree (tA);
    const tDuree = tChrono.duree_ms;
    const tCache = {reglages : tA, chronologie : tChrono, vitesseMax : vitesse_max_easing (tA.easing, tA.depassement_pct)};
    if (tDuree <= 0)
      return {duree_ms : 0, images : [{offset : 1, ...echantillon_animation_entree (tA, pContexte, 0, tCache)}]};
    const tNombre = Math.max (2, Math.ceil ((tDuree / 1000) * Math.max (10, pImagesParSeconde)) + 1);
    const tImages = [];
    for (let i = 0; i < tNombre; i++)
      {
        const tOffset = i / (tNombre - 1);
        tImages.push ({offset : tOffset, ...echantillon_animation_entree (tA, pContexte, tOffset * tDuree, tCache)});
      }
    return {duree_ms : tDuree, images : tImages};
  }
